import React from 'react';
import { cx } from '../cx';
import { Button } from './Button';
import { IconSite, IconError } from '../icons';

// D-09 §15.37 — 빈 상태·에러 상태 컴포넌트 (.state-block)
// 높이(D-09 v2.5): .state-block 은 height:100% 라 자기가 대체하는 영역을 채운다.
// 부모 높이가 auto 면 100% 가 auto 로 풀려 콘텐츠 높이로 서므로, 영역을 실제로 채우려면
// 감싸는 쪽이 높이를 준다(그리드·플렉스 셀은 stretch 기본이라 그대로 채워진다).
// 상태 전환 규칙(D-07 §17): 요청→Loading(Skeleton) → 데이터 있음 Content / 0건 EmptyState / 실패 ErrorState.
// 로딩·빈 상태 혼용 금지, spinner 단독 금지.

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 이모지 문자열이면 34px 글자로, ReactNode(SVG 등)면 아이콘 칩(.ico-chip)으로 렌더한다.
   *  기본은 위치·현장(IconSite). 사진 화면은 <IconPhoto /> 를 넘긴다. */
  icon?: React.ReactNode;
  title: string;
  description?: string;
  /** 주 행동 버튼 등 — 없으면 미표시 */
  action?: React.ReactNode;
}

export function EmptyState({ icon = <IconSite />, title, description, action, className = '', ...props }: EmptyStateProps) {
  return (
    <div className={cx('state-block', className)} {...props}>
      <span className={typeof icon === 'string' ? 'ico' : 'ico-chip'} aria-hidden="true">{icon}</span>
      <div className="state-block-text">
        <h4>{title}</h4>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

export interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** EmptyState 와 동일 규칙. 칩으로 렌더될 땐 .is-error(errorSubtle 배경 + error 아이콘) */
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
}

export function ErrorState({
  icon = <IconError />,
  title = '불러오기에 실패했습니다',
  description,
  onRetry,
  retryLabel = '다시 시도',
  className = '',
  ...props
}: ErrorStateProps) {
  const isEmoji = typeof icon === 'string';
  return (
    <div className={cx('state-block', className)} {...props} role="alert">
      <span
        className={isEmoji ? 'ico' : 'ico-chip is-error'}
        aria-hidden="true"
        style={isEmoji ? { color: 'var(--color-error)' } : undefined}
      >
        {icon}
      </span>
      <div className="state-block-text">
        <h4>{title}</h4>
        {description && <p>{description}</p>}
      </div>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}
